/**
 * `<HandoffRoute>` — `/w/:slug/handoff`
 *
 * The live monitoring board: active conversations across the workspace, so a
 * support agent can take one over and later hand it back to the AI. Backed by
 * `GET /api/handoffs/live` (gated `conversation:monitor`); the row actions are gated
 * on `handoff:take-over` / `handoff:handback`. Polls so the board stays fresh
 * (SSE is a later slice).
 */

import { useAbility, useWorkspace } from "@cometchat-ai/auth/client";
import { Button, TableBody, TableHeader, toast } from "@cometchat-ai/ui-dashboard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Headset, Loader2 } from "../components/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import {
  DataGrid,
  DataGridCell,
  DataGridHead,
  DataGridHeaderRow,
  DataGridRow,
} from "../components/data-grid";
import { PageShell } from "../components/page-shell";
import { EmptyState } from "../components/empty-state";
import { ErrorState } from "../components/error-state";
import { ListSkeleton } from "../components/list-skeleton";
import { formatApiError } from "../lib/format-error";
import {
  type LiveSession,
  claimHandoff,
  handbackHandoff,
  liveKeys,
  listLiveSessions,
  takeOverSession,
} from "../handoff/api";
import { useHandoffBoardLiveSync } from "../handoff/use-session-stream";

function customerLabel(s: LiveSession): string {
  return s.contact?.displayName ?? s.contact?.email ?? s.title ?? s.id.slice(0, 8);
}

export function HandoffRoute() {
  const { t } = useTranslation("workspace");
  const { workspace } = useWorkspace();
  const ability = useAbility();
  const queryClient = useQueryClient();

  const canTakeOver = ability.can("take-over", "HandoffEvent");
  const canHandBack = ability.can("handback", "HandoffEvent");
  const canClaim = ability.can("claim", "HandoffEvent");

  const liveQuery = useQuery({
    queryKey: liveKeys.list(workspace.slug),
    queryFn: () => listLiveSessions(),
    // Realtime via SSE (below); a slow poll stays as a safety net for a dropped
    // stream / missed event.
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  // Live board updates over SSE — any status change in the workspace refetches.
  useHandoffBoardLiveSync(workspace.slug);

  const navigate = useNavigate();

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: liveKeys.list(workspace.slug) });

  // Drop the agent into the conversation to start replying.
  const openConversation = (sessionId: string) =>
    navigate(`/w/${workspace.slug}/conversations?c=${sessionId}`);

  const takeOver = useMutation({
    mutationFn: (sessionId: string) => takeOverSession(sessionId),
    onSuccess: (_data, sessionId) => {
      toast.success(t("handoff.tookOverToast"));
      invalidate();
      openConversation(sessionId);
    },
    onError: (err) => toast.error(t("handoff.actionFailed"), { description: formatApiError(err) }),
  });

  const handBack = useMutation({
    mutationFn: (handoffId: string) => handbackHandoff(handoffId),
    onSuccess: () => {
      toast.success(t("handoff.handedBackToast"));
      invalidate();
    },
    onError: (err) => toast.error(t("handoff.actionFailed"), { description: formatApiError(err) }),
  });

  const claim = useMutation({
    mutationFn: (vars: { handoffId: string; sessionId: string }) => claimHandoff(vars.handoffId),
    onSuccess: (_data, vars) => {
      toast.success(t("handoff.claimedToast"));
      invalidate();
      openConversation(vars.sessionId);
    },
    onError: (err) => toast.error(t("handoff.actionFailed"), { description: formatApiError(err) }),
  });

  const sessions = liveQuery.data?.items ?? [];
  const pendingId = takeOver.isPending
    ? takeOver.variables
    : handBack.isPending
      ? handBack.variables
      : claim.isPending
        ? claim.variables.sessionId
        : null;

  return (
    <PageShell className="p-0" data-testid="handoff-page" description={t("handoff.subtitle")}>
      <div className="flex flex-col py-2">
        {liveQuery.isPending ? (
          <ListSkeleton rows={3} itemClassName="h-10 w-full" className="space-y-2 px-6" />
        ) : liveQuery.error ? (
          <div className="px-6">
            <ErrorState message={liveQuery.error.message} onRetry={liveQuery.refetch} />
          </div>
        ) : sessions.length === 0 ? (
          <div className="px-6 py-12">
            <EmptyState data-testid="handoff-empty" icon={Headset} title={t("handoff.empty")} />
          </div>
        ) : (
          <DataGrid className="border-t border-border">
            <TableHeader>
              <DataGridHeaderRow>
                <DataGridHead className="pl-6">{t("handoff.table.customer")}</DataGridHead>
                <DataGridHead className="w-[150px]">{t("handoff.table.status")}</DataGridHead>
                <DataGridHead className="w-[110px]">{t("handoff.table.channel")}</DataGridHead>
                <DataGridHead className="w-[150px] pr-6 text-right" divider={false}>
                  {t("handoff.table.actions")}
                </DataGridHead>
              </DataGridHeaderRow>
            </TableHeader>
            <TableBody>
              {sessions.map((s) => {
                const busy =
                  pendingId === s.id ||
                  (s.activeHandoffId !== null && pendingId === s.activeHandoffId);
                return (
                  <DataGridRow key={s.id} data-testid={`handoff-row-${s.id}`}>
                    <DataGridCell className="pl-6">
                      <div className="min-w-0">
                        <div className="truncate text-[13px] text-foreground">
                          {customerLabel(s)}
                        </div>
                        {s.reason ? (
                          <div
                            className="truncate text-[12px] text-muted-foreground"
                            title={s.reason}
                          >
                            {s.reason}
                          </div>
                        ) : null}
                      </div>
                    </DataGridCell>
                    <DataGridCell>
                      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[12px] text-muted-foreground">
                        {t(`handoff.status.${s.status}`)}
                      </span>
                    </DataGridCell>
                    <DataGridCell className="text-[13px] text-muted-foreground">
                      {s.channel}
                    </DataGridCell>
                    <DataGridCell className="pr-6 text-right" divider={false}>
                      {s.status === "human_handling" && s.activeHandoffId ? (
                        canHandBack ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={busy}
                            onClick={() => handBack.mutate(s.activeHandoffId!)}
                            className="h-7 gap-1.5 rounded-md text-[13px]"
                            data-testid={`handoff-handback-${s.id}`}
                          >
                            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                            {t("handoff.handBack")}
                          </Button>
                        ) : null
                      ) : s.status === "queued" && s.activeHandoffId ? (
                        canClaim ? (
                          <Button
                            size="sm"
                            disabled={busy}
                            onClick={() =>
                              claim.mutate({ handoffId: s.activeHandoffId!, sessionId: s.id })
                            }
                            className="h-7 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
                            data-testid={`handoff-claim-${s.id}`}
                          >
                            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                            {t("handoff.claim")}
                          </Button>
                        ) : null
                      ) : s.status === "ai_handling" ? (
                        canTakeOver ? (
                          <Button
                            size="sm"
                            disabled={busy}
                            onClick={() => takeOver.mutate(s.id)}
                            className="h-7 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
                            data-testid={`handoff-takeover-${s.id}`}
                          >
                            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                            {t("handoff.takeOver")}
                          </Button>
                        ) : null
                      ) : null}
                    </DataGridCell>
                  </DataGridRow>
                );
              })}
            </TableBody>
          </DataGrid>
        )}
      </div>
    </PageShell>
  );
}
